/**
 * Ek mesai talebi saat aralıkları (QR Mesai Faz 2, 18.10.2026).
 * Çalışma Saatleri'ndeki "Talep saat aralıkları" listesi sunucuya "HH:mm-HH:mm" (kısa çizgi) olarak gider;
 * ekranda "18:00–22:00" (uzun çizgi) gösterilir.
 */

export interface SaatAraligi {
    /** "HH:mm" */
    baslangic: string
    /** "HH:mm" */
    bitis: string
}

const SAAT = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** "HH:mm" biçiminde geçerli bir saat mi (00:00–23:59). */
export const saatGecerliMi = (s: string | null | undefined): boolean => !!s && SAAT.test(s.trim());

/** "HH:mm" → gün başından dakika; geçersizse null. */
export const saatDakikasi = (s: string | null | undefined): number | null => {
    if (!saatGecerliMi(s))
        return null;

    var p = s!.trim().split(':');
    return Number(p[0]) * 60 + Number(p[1]);
}

/**
 * "08:00-18:00" / "08:00 – 18:00" / "08:00–18:00" → { baslangic, bitis }.
 * Biçim tutmazsa null (bitişin başlangıçtan sonra olup olmadığına bakmaz; bkz. saatAraligiHatasi).
 */
export const saatAraligiCoz = (metin: string | null | undefined): SaatAraligi | null => {
    if (!metin)
        return null;

    var p = metin.split(/\s*[-–—]\s*/);

    if (p.length != 2 || !saatGecerliMi(p[0]) || !saatGecerliMi(p[1]))
        return null;

    return { baslangic: p[0].trim(), bitis: p[1].trim() };
}

/** Sunucuya giden biçim: "08:00-18:00". */
export const saatAraligiMetni = (baslangic: string, bitis: string) => `${baslangic}-${bitis}`;

/** Ekranda gösterilen biçim: "08:00–18:00". */
export const saatAraligiEtiketi = (baslangic: string | null | undefined, bitis: string | null | undefined) =>
    baslangic && bitis ? `${baslangic}–${bitis}` : '';

/** Tek aralığın kontrolü. Hata metni döner; sorun yoksa undefined. */
export const saatAraligiHatasi = (baslangic: string | null | undefined, bitis: string | null | undefined): string | undefined => {
    if (!saatGecerliMi(baslangic) || !saatGecerliMi(bitis))
        return 'Başlangıç ve bitiş saatleri SS:dd biçiminde girilmelidir (ör. 18:00).';

    if (saatDakikasi(bitis)! <= saatDakikasi(baslangic)!)
        return `${baslangic}–${bitis}: Bitiş saati başlangıçtan sonra olmalıdır.`;

    return undefined;
}

/**
 * Çalışma Saatleri'ndeki listenin tamamının kontrolü: en az bir aralık, her biri geçerli ve tekrarsız.
 * Hata metni döner; sorun yoksa undefined.
 */
export const talepAraliklariHatasi = (araliklar: string[] | null | undefined): string | undefined => {
    var liste = araliklar ?? [];

    if (liste.length == 0)
        return 'En az bir talep saat aralığı girilmelidir.';

    var gorulen = new Set<string>();

    for (var a of liste) {
        var c = saatAraligiCoz(a);

        if (!c)
            return `"${a}" geçerli bir saat aralığı değil (SS:dd–SS:dd).`;

        var hata = saatAraligiHatasi(c.baslangic, c.bitis);

        if (hata)
            return hata;

        var anahtar = saatAraligiMetni(c.baslangic, c.bitis);

        if (gorulen.has(anahtar))
            return `${saatAraligiEtiketi(c.baslangic, c.bitis)} aralığı iki kez girilmiş.`;

        gorulen.add(anahtar);
    }

    return undefined;
}

/** Listeyi sunucu biçimine çevirir, başlangıç sonra bitiş saatine göre sıralar. Geçersiz satırlar atlanır. */
export const talepAraliklariniDuzenle = (araliklar: string[]): string[] =>
    araliklar
        .map(saatAraligiCoz)
        .filter((x): x is SaatAraligi => x != null)
        .sort((a, b) => saatDakikasi(a.baslangic)! - saatDakikasi(b.baslangic)! || saatDakikasi(a.bitis)! - saatDakikasi(b.bitis)!)
        .map(x => saatAraligiMetni(x.baslangic, x.bitis));

/** Sunucudan liste gelmezse (eski kayıt) önerilen varsayılan üç aralık. */
export const VARSAYILAN_TALEP_ARALIKLARI = ['08:00-18:00', '18:00-22:00', '08:00-22:00'];
